import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './flip-score.module.scss'
import { useLeaderboardState } from '@store'

const DIGITS = Array.from({ length: 10 }, (_, i) => String(i))
const REPEATS = 5
const SAFE_START = 10

type Direction = 'up' | 'down'

function toDigitChar(v: string) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '0'
  return String(((n % 10) + 10) % 10)
}

function DrumDigit({
  value,
  dir,
  moveId,
}: {
  value: string
  dir: Direction
  moveId: number
}) {
  const targetChar = toDigitChar(value)
  const target = Number(targetChar)

  const [pos, setPos] = useState(() => SAFE_START + target)
  const [transitionOn, setTransitionOn] = useState(true)

  const posRef = useRef(pos)
  useEffect(() => {
    posRef.current = pos
  }, [pos])

  const lastMoveIdRef = useRef<number>(-1)

  useEffect(() => {
    if (lastMoveIdRef.current === moveId) return
    lastMoveIdRef.current = moveId

    const curDigit = ((posRef.current % 10) + 10) % 10
    if (curDigit === target) return

    const forwardSteps = (target - curDigit + 10) % 10 // 0..9
    const backwardSteps = -((curDigit - target + 10) % 10) // 0..-9

    const delta = dir === 'down' ? backwardSteps : forwardSteps
    if (delta === 0) return

    setPos((p) => p + delta)
  }, [target, dir, moveId])

  const onTransitionEnd = () => {
    const p = posRef.current
    const curDigit = ((p % 10) + 10) % 10
    const resetPos = SAFE_START + curDigit

    const min = 10
    const max = 10 * (REPEATS - 2)

    if (p >= min && p < max) return

    setTransitionOn(false)
    setPos(resetPos)
    setTimeout(() => {
      setTransitionOn(true)
    }, 1000)
  }

  const items = useMemo(() => {
    const arr: string[] = []
    for (let r = 0; r < REPEATS; r++) arr.push(...DIGITS)
    return arr
  }, [])

  return (
    <div className={styles.drumWindow}>
      <div
        className={`${styles.drum} ${transitionOn ? styles.drumAnimating : ''}`}
        style={{ transform: `translateY(${-pos * 50}rem)` }}
        onTransitionEnd={onTransitionEnd}
      >
        {items.map((d, i) => (
          <div key={i} className={styles.drumItem}>
            {d}
          </div>
        ))}
      </div>
    </div>
  )
}

function FlipNumbers(props: { value: number; minDigits?: number }) {
  const valueInt = Math.max(0, Math.floor(props.value))

  const prevValueRef = useRef<number>(valueInt)

  const [move, setMove] = useState<{ id: number; dir: Direction }>({
    id: 0,
    dir: 'up',
  })

  useEffect(() => {
    const prev = prevValueRef.current
    const next = valueInt
    if (next === prev) return

    const dir: Direction = next > prev ? 'up' : 'down'
    prevValueRef.current = next
    setMove((m) => ({ id: m.id + 1, dir }))
  }, [valueInt])

  const digits = useMemo(() => {
    const s = String(valueInt)
    const padded = props.minDigits ? s.padStart(props.minDigits, '0') : s
    return padded.split('')
  }, [valueInt, props.minDigits])

  return (
    <div className={styles.wrapper}>
      {digits.map((d, i) => (
        <DrumDigit
          key={`${digits.length}-${i}`}
          value={d}
          dir={move.dir}
          moveId={move.id}
        />
      ))}
      <svg className={styles.not} width="49" height="27" viewBox="0 0 49 27" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_ii_6106_13330)">
          <rect width="49" height="27" rx="6" fill="url(#paint0_linear_6106_13330)"/>
          <path d="M12.0691 19.358V18.112C11.4158 18.0093 10.8628 17.769 10.4101 17.391C9.96209 17.0083 9.64943 16.5627 9.47209 16.054L10.5501 15.662C10.7414 16.0867 11.0238 16.432 11.3971 16.698C11.7704 16.9593 12.2114 17.09 12.7201 17.09C13.2848 17.09 13.7304 16.95 14.0571 16.67C14.3884 16.39 14.5541 15.9747 14.5541 15.424C14.5541 15.0507 14.4584 14.731 14.2671 14.465C14.0758 14.1943 13.8238 13.9633 13.5111 13.772C13.2031 13.5807 12.8718 13.415 12.5171 13.275C12.1904 13.149 11.8638 13.0067 11.5371 12.848C11.2104 12.6847 10.9141 12.4933 10.6481 12.274C10.3821 12.0547 10.1674 11.791 10.0041 11.483C9.84543 11.1703 9.76609 10.8017 9.76609 10.377C9.76609 9.803 9.96676 9.299 10.3681 8.865C10.7694 8.42633 11.3364 8.16267 12.0691 8.074V6.961H13.2241V8.088C13.7701 8.172 14.2204 8.36333 14.5751 8.662C14.9344 8.956 15.1724 9.306 15.2891 9.712L14.2321 10.069C14.1201 9.80767 13.9218 9.58133 13.6371 9.39C13.3524 9.19867 12.9861 9.103 12.5381 9.103C12.0434 9.103 11.6561 9.21733 11.3761 9.446C11.0961 9.67 10.9561 9.978 10.9561 10.37C10.9561 10.6827 11.0494 10.9487 11.2361 11.168C11.4274 11.3873 11.6771 11.5787 11.9851 11.742C12.2978 11.9053 12.6314 12.057 12.9861 12.197C13.3081 12.323 13.6301 12.4723 13.9521 12.645C14.2788 12.8177 14.5774 13.0277 14.8481 13.275C15.1188 13.5177 15.3358 13.814 15.4991 14.164C15.6624 14.5093 15.7441 14.92 15.7441 15.396C15.7441 15.9327 15.6368 16.3947 15.4221 16.782C15.2074 17.1693 14.9088 17.4773 14.5261 17.706C14.1481 17.9347 13.7141 18.0747 13.2241 18.126V19.358H12.0691ZM17.602 18V8.2H18.743L22.131 15.172C22.1637 15.2327 22.2034 15.3143 22.25 15.417C22.2967 15.515 22.3364 15.6013 22.369 15.676C22.3644 15.5453 22.3597 15.445 22.355 15.375C22.3504 15.3003 22.348 15.228 22.348 15.158V8.2H23.468V18H22.348L18.953 11.126C18.9204 11.0513 18.8784 10.965 18.827 10.867C18.7804 10.7643 18.7407 10.6757 18.708 10.601C18.7174 10.7083 18.722 10.804 18.722 10.888C18.7267 10.9673 18.729 11.042 18.729 11.112V18H17.602ZM28.462 18.168C27.8553 18.168 27.342 18.0233 26.922 17.734C26.502 17.44 26.1637 17.0503 25.907 16.565C25.6503 16.075 25.4637 15.5313 25.347 14.934C25.2303 14.332 25.172 13.7207 25.172 13.1C25.172 12.4793 25.2303 11.8703 25.347 11.273C25.4637 10.671 25.6503 10.1273 25.907 9.642C26.1637 9.152 26.502 8.76233 26.922 8.473C27.342 8.179 27.8553 8.032 28.462 8.032C29.0687 8.032 29.582 8.179 30.002 8.473C30.422 8.76233 30.7603 9.152 31.017 9.642C31.2737 10.1273 31.4603 10.671 31.577 11.273C31.6983 11.8703 31.759 12.4793 31.759 13.1C31.759 13.7207 31.6983 14.332 31.577 14.934C31.4603 15.5313 31.2737 16.075 31.017 16.565C30.7603 17.0503 30.422 17.44 30.002 17.734C29.582 18.0233 29.0687 18.168 28.462 18.168ZM28.462 17.111C28.8447 17.111 29.169 16.9943 29.435 16.761C29.701 16.523 29.9157 16.208 30.079 15.816C30.247 15.424 30.3683 14.9923 30.443 14.521C30.5177 14.045 30.555 13.5713 30.555 13.1C30.555 12.582 30.5177 12.085 30.443 11.609C30.3683 11.1283 30.2493 10.699 30.086 10.321C29.9227 9.943 29.708 9.64433 29.442 9.425C29.176 9.201 28.8493 9.089 28.462 9.089C28.0793 9.089 27.755 9.208 27.489 9.446C27.223 9.67933 27.006 9.992 26.838 10.384C26.6747 10.7713 26.5557 11.203 26.481 11.679C26.4063 12.1503 26.369 12.624 26.369 13.1C26.369 13.6133 26.4063 14.1103 26.481 14.591C26.5557 15.067 26.6747 15.4963 26.838 15.879C27.006 16.257 27.223 16.558 27.489 16.782C27.755 17.0013 28.0793 17.111 28.462 17.111ZM35.815 18V9.299H33.001V8.2H39.777V9.299H36.963V18H35.815Z" fill="url(#paint1_linear_6106_13330)"/>
        </g>
        <defs>
          <filter id="filter0_ii_6106_13330" x="0" y="0" width="49" height="27" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="shape" result="effect1_innerShadow_6106_13330"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="-1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="effect1_innerShadow_6106_13330" result="effect2_innerShadow_6106_13330"/>
          </filter>
          <linearGradient id="paint0_linear_6106_13330" x1="49" y1="13.5" x2="-1.48904e-06" y2="13.5" gradientUnits="userSpaceOnUse">
          <stop/>
          <stop offset="1" stopColor="#393939"/>
          </linearGradient>
          <linearGradient id="paint1_linear_6106_13330" x1="10.8947" y1="-0.538461" x2="43.3043" y2="8.10326" gradientUnits="userSpaceOnUse">
            <stop stopColor="white"/>
            <stop offset="1" stopColor="#4F4F4F"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}

export function FlipScore() {
  const { totalScore } = useLeaderboardState()
  return <FlipNumbers value={totalScore} minDigits={6} />
}